/**
 * Status lifecycle configuration for the Open311-inspired
 * report status enum. Used by the wizard, tracking page, and staff workspace.
 */
export interface StatusConfig {
  label: string;
  /** Tailwind badge classes (bg + text) */
  badgeClass: string;
  /** Tailwind dot color */
  dotClass: string;
  /** Whether the status is terminal (no further transitions) */
  terminal: boolean;
}

export const STATUS_CONFIG: Record<string, StatusConfig> = {
  submitted: { label: 'Submitted', badgeClass: 'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300', dotClass: 'bg-blue-500', terminal: false },
  acknowledged: { label: 'Acknowledged', badgeClass: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-950 dark:text-cyan-300', dotClass: 'bg-cyan-500', terminal: false },
  in_progress: { label: 'In Progress', badgeClass: 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300', dotClass: 'bg-amber-500', terminal: false },
  scheduled: { label: 'Scheduled', badgeClass: 'bg-purple-100 text-purple-800 dark:bg-purple-950 dark:text-purple-300', dotClass: 'bg-purple-500', terminal: false },
  resolved: { label: 'Resolved', badgeClass: 'bg-green-100 text-green-800 dark:bg-green-950 dark:text-green-300', dotClass: 'bg-green-500', terminal: true },
  closed: { label: 'Closed', badgeClass: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300', dotClass: 'bg-gray-500', terminal: true },
  rejected: { label: 'Rejected', badgeClass: 'bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-300', dotClass: 'bg-red-500', terminal: true },
  duplicate: { label: 'Duplicate', badgeClass: 'bg-orange-100 text-orange-800 dark:bg-orange-950 dark:text-orange-300', dotClass: 'bg-orange-500', terminal: true }
};

export function getStatusConfig(status: string | undefined): StatusConfig {
  if (status && STATUS_CONFIG[status]) return STATUS_CONFIG[status];
  return { label: status ?? 'Unknown', badgeClass: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300', dotClass: 'bg-gray-400', terminal: false };
}

export const SEVERITY_LABELS: Record<string, string> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  urgent: 'Urgent'
};

export const SEVERITY_COLORS: Record<string, string> = {
  low: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
  medium: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-950 dark:text-yellow-300',
  high: 'bg-orange-100 text-orange-800 dark:bg-orange-950 dark:text-orange-300',
  urgent: 'bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-300'
};

/** Format an ISO date string as a human-readable date/time. */
export function formatDateTime(iso: string | undefined): string {
  if (!iso) return '';
  return new Intl.DateTimeFormat('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(new Date(iso));
}

/** Format an ISO date string as a relative time (e.g. "3 hours ago"). */
export function formatRelativeTime(iso: string | undefined): string {
  if (!iso) return '';
  const now = Date.now();
  const then = new Date(iso).getTime();
  const diff = now - then;
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;
  return formatDateTime(iso);
}

/** Format a distance in metres as a human-readable string. */
export function formatDistance(metres: number | undefined): string {
  if (metres == null) return '';
  if (metres < 1000) return `${Math.round(metres)}m away`;
  return `${(metres / 1000).toFixed(1)}km away`;
}
